// ─── Programs: pure helpers shared by the marketplace (Plan → Programs) and the
// coach-assigned plan (Phase C / A1). No imports so tests/program.test.mjs can load
// this file directly in Node.

// Program day names → the schedule types the calendar, coach and analytics understand.
// Applying "Upper Power" verbatim used to leave grey calendar cells, an empty exercise
// list and a Full Body fallback in the coach.
export const KNOWN_TYPES = ["Rest", "Chest", "Back", "Legs", "Shoulders", "Arms", "Core", "Cardio", "Push", "Pull", "Upper", "Lower", "Full Body", "Custom"];
const PROGRAM_DAY_TYPE = { "Upper Power": "Upper", "Upper Hyper": "Upper", "Lower Power": "Lower", "Lower Hyper": "Lower",
  "Bench/OHP": "Push", "OHP/Incline": "Push", "Squat/Sumo": "Lower", "Deadlift/Front Squat": "Lower",
  "Squat": "Legs", "Bench": "Chest", "Deadlift": "Back", "OHP": "Shoulders" };
export const programDayType = (label) => KNOWN_TYPES.includes(label) ? label : (PROGRAM_DAY_TYPE[label] || "Custom");

// Cache of the member's `my_program` response: { assignment_id, fetched_at, data, prompted }
// `prompted` is the assignment the schedule offer was answered for (never re-asked).
export const PROGRAM_CACHE_KEY = "ft-org-program";
export const PROGRAM_REFRESH_MS = 10 * 60 * 1000;
export const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// A program's 7-label schedule (0 = Sunday) → s.schedule, exactly like ProgramMarketplace.doApply:
// every label normalised through programDayType, overrides cleared.
export function scheduleFromProgram(schedule) {
  const weekly = {};
  for (let i = 0; i < 7; i++) {
    const label = Array.isArray(schedule) ? schedule[i] : null;
    weekly[i] = typeof label === "string" && label ? programDayType(label) : "Rest";
  }
  return { weekly, overrides: {} };
}

// Next non-rest day after `fromDow` (0-6) → { label, dow, offset } or null when the whole week rests.
export function nextTrainingDay(schedule, fromDow) {
  if (!Array.isArray(schedule)) return null;
  for (let i = 1; i <= 7; i++) {
    const dow = (fromDow + i) % 7;
    const label = schedule[dow];
    if (label && label !== "Rest") return { label, dow, offset: i };
  }
  return null;
}

// What the "From your coach" card should show, decided from a `my_program` response.
// The server owns the date rule: `program`/`today_*` describe the plan running TODAY, while
// `scheduled_program` + `starts_on` describe one assigned but not yet started. A plan that
// has not started has no session today — nothing to start and no schedule to adopt.
//   data             — the `my_program` response, or null when it has not loaded (offline / first boot)
//   dow              — the member's LOCAL day of week (0 = Sunday); their calendar beats the server's UTC label
//   fallbackLabel    — `me.program.today_label`, used only while `data` is missing (null for a queued plan)
//   fallbackStarted  — `me.program.started`; false means the plan is queued, so there is no session today
//   fallbackStartsOn — `me.program.start_date`, shown while `data` is missing
// Returns { state: 'upcoming' | 'rest' | 'training', label, exercises, canStart, canAdopt, startsOn, name }.
export function planView(data, { dow = 0, fallbackLabel = null, fallbackStarted = null, fallbackStartsOn = null } = {}) {
  const program = data && data.program ? data.program : null;
  const scheduled = data && data.scheduled_program ? data.scheduled_program : null;
  const hasScheduled = !!(scheduled || (data && data.scheduled_assignment));
  if (!program && hasScheduled) {
    return { state: 'upcoming', label: null, exercises: [], canStart: false, canAdopt: false,
      startsOn: (data && data.starts_on) || null, name: (scheduled && scheduled.name) || null };
  }
  // No my_program yet: the `me` summary is all there is. started === false is a queued plan —
  // show it as upcoming instead of offering the (null) weekday label as today's session.
  if (!data && fallbackStarted === false) {
    return { state: 'upcoming', label: null, exercises: [], canStart: false, canAdopt: false,
      startsOn: fallbackStartsOn || null, name: null };
  }
  const label = (program && Array.isArray(program.schedule) ? program.schedule[dow] : null) ||
    (data && data.today_label) || fallbackLabel || 'Rest';
  const rest = label === 'Rest';
  const fromProgram = program && program.days ? program.days[label] : null;
  const exercises = rest ? [] : (fromProgram || (data && data.today_exercises) || []);
  return { state: rest ? 'rest' : 'training', label, exercises, canStart: !rest, canAdopt: !!program,
    startsOn: null, name: (program && program.name) || null };
}

// "8-12" → "8" (the logger's reps input is numeric; the range is kept in the note)
const repsValue = (reps) => {
  if (typeof reps === "number") return String(reps);
  if (typeof reps === "string") { const m = /^(\d+)/.exec(reps.trim()); return m ? m[1] : ""; }
  return "";
};
const numStr = (v) => (v === null || v === undefined || v === "" || Number.isNaN(Number(v))) ? "" : String(v);

// The `ft-coach-draft` blob WorkoutTab consumes (same shape as AdaptiveCoach's handoff in
// coaching.jsx) built from `my_program.today_exercises`. Exercises without an `exerciseId`
// are matched by name against the catalogue in `meta.exercises`; rows the catalogue does not
// know are dropped (the caller compares lengths to warn). Weight absent → "" so the logger's
// last-sets lookup (findLastSets) fills it, as it does for a hand-picked exercise.
export function draftFromProgram(todayExercises, meta = {}) {
  const catalogue = Array.isArray(meta.exercises) ? meta.exercises : [];
  const byId = new Map(catalogue.map(e => [e.id, e]));
  const byName = new Map(catalogue.map(e => [String(e.name || "").toLowerCase(), e]));
  const exercises = [];
  for (const row of Array.isArray(todayExercises) ? todayExercises : []) {
    if (!row || typeof row !== "object") continue;
    let id = typeof row.exerciseId === "string" ? row.exerciseId.trim() : "";
    if (id && catalogue.length && !byId.has(id)) id = "";
    if (!id && row.name) id = byName.get(String(row.name).toLowerCase())?.id || "";
    if (!id) continue;
    const n = Math.max(1, Math.min(20, parseInt(row.sets) || 1));
    const reps = repsValue(row.reps);
    const weight = numStr(row.weight);
    const noteParts = [];
    if (typeof row.reps === "string" && /-/.test(row.reps)) noteParts.push(`${row.reps} reps`);
    if (row.rest_sec) noteParts.push(`rest ${row.rest_sec}s`);
    if (row.note) noteParts.push(String(row.note));
    const ex = { exerciseId: id, sets: Array.from({ length: n }, () => ({ weight, reps, rpe: "", done: false })) };
    if (noteParts.length) ex.note = noteParts.join(" · ");
    exercises.push(ex);
  }
  return {
    date: meta.date || "", dur: "", exercises,
    notes: meta.program_name ? [meta.program_name, meta.program_day].filter(Boolean).join(" · ") : "",
    rating: 3,
    assignment_id: meta.assignment_id || null,
    program_day: meta.program_day || null,
  };
}
